/* eslint-disable import/no-cycle */
import { call, put, all, takeLatest } from 'redux-saga/effects';
import {
    ICreateEvent,
    IEventFilter,
    IGetEventDetails,
    IGetEvents,
    IGetSimilarEvents,
    IPurchaseTicket,
    IUpdateEvent,
    IGetManageEvent,
    types,
    IManagedEventResponse,
    ICreateDiscountCode,
    IUpdateDiscount,
    ICancelEvent,
    IGetEventInviteDetails,
    IAcceptEventInvite,
} from './types';
import {
    approveEventFail,
    approveEventSuccess,
    createEventFail,
    createEventSuccess,
    deleteEventFail,
    deleteEventSuccess,
    filterEventsFail,
    filterEventsSuccess,
    getEventsDetailsFail,
    getEventsDetailsSuccess,
    getEventsFail,
    getEventsSuccess,
    getManageEventFail,
    getManageEventSuccess,
    getSimilarEventsFail,
    getSimilarEventsSuccess,
    publishEventFail,
    publishEventSuccess,
    purchaseTicketFail,
    purchaseTicketSuccess,
    updateEvent as updateEvenAction,
    updateEventFail,
    updateEventSuccess,
    getSimilarEvents as getSimilarEventAction,
    unpublishEventFail,
    unpublishEventSuccess,
    publishEvent as publishEventAction,
    createDiscountCodeSuccess,
    createDiscountCodeFail,
    updateDiscountCodeSuccess,
    updateDiscountCodeFail,
    deleteDiscountCodeSuccess,
    deleteDiscountCodeFail,
    cancelEventFail,
    cancelEventSuccess,
    getEventInviteDetailsSuccess,
    getEventInviteDetailsFail,
    acceptEventInviteSuccess,
    acceptEventInviteFail,
} from './action';
import { Action } from 'redux';
import { api } from '../../configurations/api';
import {
    eventsUrl,
    getRedeemableCodesUrl,
    ticketsUrl,
    cancelEventUrl,
    getEventInviteDetailsUrl,
    acceptEventInviteUrl,
} from '../../configurations/api/url';
import { globalErrorHandler } from '../error/saga';
import { httpRequest } from '../types';
import { SentryCapture } from '../../analytics/Sentry';
import { getNotUniqueCacheByKey, isUseCacheEnabled } from '../cache/saga';
import { CACHE_TYPE, NotUniqueCacheValue } from '../cache/types';
import { setCache } from '../cache/action';
import { uploadFilesSaga } from '../upload/saga';
import { showToastMessage, transformObjectOrArray } from '../../utils/AppUtils';
import { IGenericDocumentModifier, ITicket } from '../../types/global/helper';

function* filterEvents({ payload, resolve, reject }: { payload: IEventFilter; resolve: any; reject: any }): any {
    try {
        const request = `${eventsUrl}/filter`;
        const response = yield call(api, request, httpRequest.POST, payload, 3, null);
        yield put(filterEventsSuccess(response));
        if (resolve) resolve(response);
    } catch (error: any) {
        SentryCapture(error, 'error');
        yield put(filterEventsFail(error));
        if (reject) reject();
    }
}

const parseEventPayload = (payload: ICreateEvent, isCreate: boolean = false): Record<any, any> => {
    const final: any = { body: {}, feature_image: null };
    Object.keys(payload).forEach(key => {
        const currentKey = key as keyof ICreateEvent;
        switch (currentKey) {
            case 'feature_image':
                final.feature_image = payload[currentKey];
                break;
            case 'fee_structure':
                if (isCreate) {
                    final.body['fee_structure'] = payload['fee_structure']?.map(fees => ({
                        ...fees,
                        fee: Number(fees.fee) * 100,
                    }));
                } else {
                    // For updates, fee is already in cents from the component
                    final.body['fee_structure'] = payload['fee_structure'];
                }
                break;
            default:
                final.body[currentKey] = payload[currentKey];
                break;
        }
    });

    return final;
};

function* createDiscountCode({ payload }: { payload: ICreateDiscountCode }): Generator<any, void, any> {
    try {
        const response = yield call(api, `${getRedeemableCodesUrl}/discount`, httpRequest.POST, payload);
        const { data } = response;
        yield put(createDiscountCodeSuccess(data));
    } catch (error: any) {
        yield put(createDiscountCodeFail(error));
        SentryCapture(error, 'error');
        yield call(globalErrorHandler, error);
    }
}

function* updateDiscountCode({ payload }: { payload: IUpdateDiscount }): Generator<any, void, any> {
    try {
        const response = yield call(api, `${getRedeemableCodesUrl}/${payload.id}`, httpRequest.PATCH, payload.body);
        const { data } = response.data;
        yield put(updateDiscountCodeSuccess(data));
    } catch (error: any) {
        yield put(updateDiscountCodeFail(error));
        SentryCapture(error, 'error');
        yield call(globalErrorHandler, error);
    }
}

function* deleteDiscountCode({ payload }: { payload: string }): Generator<any, void, any> {
    try {
        yield call(api, `${getRedeemableCodesUrl}/${payload}`, httpRequest.DELETE, {});
        yield put(deleteDiscountCodeSuccess(payload));
    } catch (error: any) {
        yield put(deleteDiscountCodeFail(error));
        SentryCapture(error, 'error');
        yield call(globalErrorHandler, error);
    }
}

function* createEvent({ payload }: { payload: ICreateEvent }): Generator<any, any, any> {
    const { body, feature_image } = parseEventPayload(payload, true);
    try {
        const response = yield call(api, eventsUrl, httpRequest.POST, transformObjectOrArray(body), 0, 0);
        const { data } = response.data;
        const { _id } = data;
        if (feature_image && feature_image !== 'no-image') {
            const imageUpload = yield call(uploadFilesSaga, {
                payload: { collection: 'event', directory: 'media', path: 'image', files: feature_image, id: _id },
            });

            yield put(
                updateEvenAction({
                    id: _id,
                    body: {
                        feature_image: imageUpload[0],
                    },
                    fromCreate: true,
                }),
            );
        } else {
            yield put(createEventSuccess(data));
            yield call(showToastMessage, 'Event created successfully', 'success');
        }
        if (payload.publishStatus === 'publish') {
            yield put(
                publishEventAction({
                    documentId: _id,
                }),
            );
        }
    } catch (error: any) {
        SentryCapture(error, 'error');
        yield put(createEventFail(error));
        yield call(globalErrorHandler, error);
    }
}

function* updateEvent({ payload }: { payload: IUpdateEvent }): Generator<any, any, any> {
    const { id, body, fromCreate } = payload;
    let requestUrl = `${eventsUrl}/${id}`;

    try {
        if (fromCreate) {
            const response = yield call(api, requestUrl, httpRequest.PATCH, body, 0, 0);
            const { data } = response.data;
            yield put(createEventSuccess(data));
            yield call(showToastMessage, 'Event created successfully', 'success');
            yield put(createEventSuccess(data));
        } else {
            const { body: updateBody, feature_image } = parseEventPayload(body);
            if (feature_image && feature_image !== 'no-image') {
                const imageUpload = yield call(uploadFilesSaga, {
                    payload: { collection: 'event', directory: 'media', path: 'image', files: feature_image, id: id },
                });
                updateBody.feature_image = imageUpload[0];
            }
            const response = yield call(api, requestUrl, httpRequest.PATCH, updateBody, 0, 0);
            const { data } = response.data;
            yield put(updateEventSuccess(data));
            yield call(showToastMessage, 'Event updated successfully', 'success');
        }
    } catch (error: any) {
        SentryCapture(error, 'error');
        yield put(updateEventFail(error));
        yield call(globalErrorHandler, error);
    }
}

function* publishEvent({ payload }: { payload: IGenericDocumentModifier }): any {
    const requestUrl = `${eventsUrl}/publish/${payload.documentId}`;
    try {
        const response = yield call(api, requestUrl, httpRequest.PATCH, {}, 0, 0);
        const { data } = response;
        yield put(publishEventSuccess(data));
        yield call(showToastMessage, 'Event published successfully', 'success');
    } catch (error: any) {
        SentryCapture(error, 'error');
        yield put(publishEventFail(error));
        yield call(globalErrorHandler, error);
    }
}

function* unPublishEvent({ payload }: { payload: IGenericDocumentModifier }): any {
    const requestUrl = `${eventsUrl}/unpublish/${payload.documentId}`;
    try {
        const response = yield call(api, requestUrl, httpRequest.PATCH, {}, 0, 0);
        const { data } = response;
        yield put(unpublishEventSuccess(data));
        yield call(showToastMessage, 'Event unpublished successfully', 'success');
    } catch (error: any) {
        SentryCapture(error, 'error');
        yield put(unpublishEventFail(error));
        yield call(globalErrorHandler, error);
    }
}

function* deleteEvent({ payload }: any): any {
    const requestUrl = `${eventsUrl}/${payload}`;
    try {
        yield call(api, requestUrl, httpRequest.DELETE, {}, 0, 0);
        yield put(deleteEventSuccess(payload));
    } catch (error: any) {
        SentryCapture(error, 'error');
        yield put(deleteEventFail(error));
        yield call(globalErrorHandler, error);
    }
}

function* getAllEvents({
    payload,
    resolve,
    reject,
}: {
    payload: IGetEvents;
    resolve: any;
    reject: any;
}): Generator<any, any, any> {
    const defaultUseCache = yield* isUseCacheEnabled();
    let initialResult: any = null;
    let requestUrl = eventsUrl;
    const query = new URLSearchParams();
    if (payload.limit) query.append('limit', String(payload.limit));
    if (payload.page) query.append('page', String(payload.page));
    const cache: NotUniqueCacheValue = yield* getNotUniqueCacheByKey(CACHE_TYPE.EVENTS, query.toString() as string);
    if (cache && cache.key) {
        initialResult = cache.value;
    }
    if (initialResult && defaultUseCache) {
        yield put(getEventsSuccess(initialResult));
        if (resolve) resolve(initialResult);
    } else {
        try {
            const response = yield call(api, `${requestUrl}?${query.toString()}`, httpRequest.GET, null, 0, 0);
            yield put(getEventsSuccess(response));
            yield put(
                setCache({
                    key: query.toString(),
                    value: response,
                    type: CACHE_TYPE.EVENTS,
                    isUnique: false,
                }),
            );
            if (resolve) resolve(response);
        } catch (error: any) {
            SentryCapture(error, 'error');
            yield put(getEventsFail(error));
            if (reject) reject(error);
        }
    }
}

function* getSimilarEvents({ payload }: { payload: IGetSimilarEvents }): Generator<any, any, any> {
    const queryParams = new URLSearchParams();
    // Conditionally add parameters
    if (payload.kind) queryParams.append('kind', String(payload.kind));
    if (payload.tags) queryParams.append('tags', String(payload.tags));
    if (payload.limit) queryParams.append('limit', String(payload.limit));
    if (payload.page) queryParams.append('page', String(payload.page));
    const requestUrl = `${eventsUrl}?${queryParams.toString()}`;

    let initialResult: any = null;
    const defaultUseCache = yield* isUseCacheEnabled();

    const cache: NotUniqueCacheValue = yield* getNotUniqueCacheByKey(CACHE_TYPE.SIMILAR_EVENTS, queryParams.toString());

    if (cache && cache.key) {
        initialResult = cache.value;
    }

    if (defaultUseCache && initialResult) {
        yield put(getSimilarEventsSuccess(initialResult));
    } else {
        try {
            const response = yield call(api, requestUrl, httpRequest.GET, null, 0, 0);
            const data = response?.data?.data ?? response?.data ?? response;
            yield put(getSimilarEventsSuccess(data));
            yield put(
                setCache({
                    key: queryParams.toString(),
                    value: data,
                    type: CACHE_TYPE.SIMILAR_EVENTS,
                    isUnique: false,
                }),
            );
        } catch (error: any) {
            SentryCapture(error, 'error');
            yield put(getSimilarEventsFail(error));
        }
    }
}

function* getEventsDetails({ payload }: { payload: IGetEventDetails }): any {
    const requestUrl = `${eventsUrl}/${payload.slug}${payload.params ? payload.params : ''}`;
    try {
        const response = yield call(api, requestUrl, httpRequest.GET, null, 0, 0);
        if (response?.status === 'failed') {
            yield put(getEventsDetailsFail(response?.message ?? 'No document found'));
            return;
        }
        const data = response?.data?.data ?? response?.data;
        if (!data) {
            yield put(
                getEventsDetailsFail({
                    data: {
                        message: 'No Event found',
                        status: 'failed',
                    },
                    statusText: 'No Event found',
                    status: 0,
                }),
            );
            return;
        }
        yield put(getEventsDetailsSuccess(data));
        yield put(
            getSimilarEventAction({
                kind: data.kind,
                limit: 2,
                page: 1,
            }),
        );
    } catch (error: any) {
        SentryCapture(error, 'error');
        const message = error?.data?.message ?? error?.message ?? 'Failed to load event details';
        yield put(getEventsDetailsFail(message));
    }
}

function* purchaseTicket({
    payload,
    resolve,
    reject,
}: {
    payload: IPurchaseTicket;
    resolve: (value: ITicket | PromiseLike<ITicket>) => void;
    reject: (reason?: any) => void;
}): any {
    try {
        const response = yield call(api, ticketsUrl, httpRequest.POST, payload, 0, 0);
        const { data } = response;
        yield put(purchaseTicketSuccess(data));
        yield call(showToastMessage, 'Ticket purchased successfully', 'success');
        resolve(data);
    } catch (error: any) {
        SentryCapture(error, 'error');
        yield call(globalErrorHandler, error);
        yield put(purchaseTicketFail(error));
        reject(error);
    }
}

function* approveEvent({ payload }: { payload: IGenericDocumentModifier }): any {
    const requestUrl = `${eventsUrl}/approve/${payload.documentId}`;
    try {
        const response = yield call(api, requestUrl, httpRequest.PATCH, {}, 0, 0);
        const { data } = response;
        yield put(approveEventSuccess(data));
    } catch (error: any) {
        SentryCapture(error, 'error');
        yield put(approveEventFail(error));
    }
}

function* getManageEvent({
    payload,
    resolve,
    reject,
}: {
    payload: IGetManageEvent;
    resolve: (value: IManagedEventResponse) => Promise<void>;
    reject: (reason: any) => Promise<void>;
}): any {
    try {
        const {
            eventId,
            salePage = 1,
            saleLimit = 20,
            ticketPage = 1,
            ticketLimit = 20,
            searchTerm = '',
            startDate,
            endDate,
            ticketType,
            sortBy = 'createdAt',
            sortOrder = 'desc',
        } = payload;

        let url = `${eventsUrl}/manage/${eventId}?salePage=${salePage}&saleLimit=${saleLimit}&ticketPage=${ticketPage}&ticketLimit=${ticketLimit}&sortOrder=${sortOrder}`;

        if (searchTerm) {
            url += `&searchTerm=${searchTerm}`;
        }

        if (startDate) {
            url += `&startDate=${startDate}`;
        }

        if (endDate) {
            url += `&endDate=${endDate}`;
        }

        if (ticketType) {
            url += `&ticketType=${ticketType}`;
        }

        if (sortBy) {
            url += `&sortBy=${sortBy}`;
        }

        const response: IManagedEventResponse = yield call(api, url, httpRequest.GET, null, 0, 0);
        yield put(getManageEventSuccess(response.data));
        if (resolve) resolve(response);
    } catch (error: any) {
        SentryCapture(error, 'error');
        yield put(getManageEventFail(error));
        if (reject) reject(error);
    }
}

function* cancelEventSaga({
    payload,
    resolve,
    reject,
}: {
    payload: ICancelEvent;
    resolve: (value: any) => void;
    reject: (reason?: any) => void;
}): Generator<any, void, any> {
    try {
        const { eventId } = payload;
        const response = yield call(api, `${cancelEventUrl}/${eventId}`, httpRequest.POST, {}, 0, 0);
        const { data } = response;
        yield put(cancelEventSuccess(data));
        yield call(showToastMessage, data.message || 'Event cancelled successfully', 'success');

        if (resolve) {
            resolve(data);
        }
    } catch (error: any) {
        SentryCapture(error, 'error');
        yield call(globalErrorHandler, error);
        yield put(cancelEventFail(error.message || 'Failed to cancel event'));

        if (reject) {
            reject(error);
        }
    }
}

function* getEventInviteDetailsSaga({
    payload,
    resolve,
    reject,
}: {
    payload: IGetEventInviteDetails;
    resolve: (data: any) => void;
    reject: (error: any) => void;
}): Generator<any, void, any> {
    try {
        const url = `${getEventInviteDetailsUrl}/${payload.token}`;
        const response = yield call(api, url, httpRequest.GET, null, 2, 5000);
        const data = response?.data ?? response;
        yield put(getEventInviteDetailsSuccess(data));
        if (resolve) resolve(data);
    } catch (error: any) {
        SentryCapture(error, 'error');
        yield put(getEventInviteDetailsFail(error));
        if (reject) reject(error);
    }
}

function* acceptEventInviteSaga({
    payload,
    resolve,
    reject,
}: {
    payload: IAcceptEventInvite;
    resolve: (data: any) => void;
    reject: (error: any) => void;
}): Generator<any, void, any> {
    try {
        const url = acceptEventInviteUrl(payload.token);
        yield call(api, url, httpRequest.PATCH, {}, 2, 5000);
        yield put(acceptEventInviteSuccess(payload.token));
        yield call(showToastMessage, 'Invitation accepted successfully', 'success');
        if (resolve) resolve({ accepted: true });
    } catch (error: any) {
        SentryCapture(error, 'error');
        yield put(acceptEventInviteFail(error));
        if (reject) reject(error);
    }
}

/** Interfaces */
interface TaskAction extends Action {
    type: string;
    payload: any;
    resolve: any;
    reject: any;
}

/** **************** WATCHERS ************************** */

function* createEventWatcher() {
    yield takeLatest<TaskAction>(types.CREATE_EVENT, createEvent);
}

function* updateEventWatcher() {
    yield takeLatest<any>(types.UPDATE_EVENT, updateEvent);
}

function* publishEventWatcher() {
    yield takeLatest<any>(types.PUBLISH_EVENT, publishEvent);
}

function* unpublishEventWatcher() {
    yield takeLatest<any>(types.UNPUBLISH_EVENT, unPublishEvent);
}

function* deleteEventWatcher() {
    yield takeLatest(types.DELETE_EVENT, deleteEvent);
}

function* getAllEventsWatcher() {
    yield takeLatest<any>(types.GET_EVENTS, getAllEvents);
}

function* filterEventWatcher() {
    yield takeLatest<any>(types.FILTER_EVENTS, filterEvents);
}

function* getEventsDetailsWatcher() {
    yield takeLatest<TaskAction>(types.GET_EVENT_DETAILS, getEventsDetails);
}

function* getSimilarEventsWatcher() {
    yield takeLatest<TaskAction>(types.GET_SIMILAR_EVENTS, getSimilarEvents);
}

function* purchaseTicketWatcher() {
    yield takeLatest<TaskAction>(types.PURCHASE_TICKET, purchaseTicket);
}

function* approveEventWatcher() {
    yield takeLatest<TaskAction>(types.APPROVE_EVENT, approveEvent);
}

function* getManageEventWatcher() {
    yield takeLatest<TaskAction>(types.GET_MANAGE_EVENT, getManageEvent);
}

function* createDiscountCodeWatcher() {
    yield takeLatest<TaskAction>(types.CREATE_DISCOUNT_CODE, createDiscountCode);
}

function* updateDiscountCodeWatcher() {
    yield takeLatest<TaskAction>(types.UPDATE_DISCOUNT, updateDiscountCode);
}

function* deleteDiscountCodeWatcher() {
    yield takeLatest<TaskAction>(types.DELETE_DISCOUNT, deleteDiscountCode);
}

function* cancelEventWatcher() {
    yield takeLatest<any>(types.CANCEL_EVENT, cancelEventSaga);
}

function* getEventInviteDetailsWatcher() {
    yield takeLatest<any>(types.GET_EVENT_INVITE_DETAILS, getEventInviteDetailsSaga);
}

function* acceptEventInviteWatcher() {
    yield takeLatest<any>(types.ACCEPT_EVENT_INVITE, acceptEventInviteSaga);
}

export default function* eventSaga() {
    yield all([
        createEventWatcher(),
        updateEventWatcher(),
        publishEventWatcher(),
        deleteEventWatcher(),
        getAllEventsWatcher(),
        getEventsDetailsWatcher(),
        getSimilarEventsWatcher(),
        filterEventWatcher(),
        unpublishEventWatcher(),
        purchaseTicketWatcher(),
        approveEventWatcher(),
        getManageEventWatcher(),
        createDiscountCodeWatcher(),
        updateDiscountCodeWatcher(),
        deleteDiscountCodeWatcher(),
        cancelEventWatcher(),
        getEventInviteDetailsWatcher(),
        acceptEventInviteWatcher(),
    ]);
}
